/**
 * Auth provider – session restore, login/2FA/logout flows, token refresh and profile sync.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { User, RegisterRequest } from '@lambda/shared/types';
import { authApi } from '@services/authApi';
import { usersApi } from '@services/usersApi';
import { SigningOutOverlay } from '@components/features/auth/SigningOutOverlay';
import { ensureMinSigningOutVisible } from '@components/features/auth/signingOutTiming';
import { useToast } from '@contexts/ToastContext';
import { setRefreshInProgress } from '@utils/authFailureHandler';
import { delay } from '@utils/delay';
import {
  SessionRefreshUnauthorizedError,
  isAccessTokenStillValid,
  readLegacyAccessToken,
  restoreSessionWithRetry,
} from '@utils/sessionRestore';
import {
  onboardingUserStub,
  onboardingUserFromLogin,
  isCompletingOnboardingSession,
} from '@utils/onboardingUser';
import { AuthContext, type AuthContextValue, type Pending2FA } from './AuthContext';

const STORAGE_KEY = 'bhg-auth';

// Only the user profile is persisted — no tokens. The refresh token lives in
// an HttpOnly cookie (set by the server); the access token lives in memory only.
function loadStored(): { user: User; legacyAccessToken?: string } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { user?: User; token?: string };
    if (parsed?.user) {
      return {
        user: parsed.user,
        legacyAccessToken:
          typeof parsed.token === 'string' && parsed.token.length > 0 ? parsed.token : undefined,
      };
    }
  } catch {
    // ignore
  }
  return null;
}

function saveStored(user: User | null) {
  if (!user) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ user }));
}

/** Fetch full profile + effectivePermissions and merge into state/storage. */
async function loadAndApplyPermissions(
  accessToken: string,
  baseUser: User,
  setUserState: React.Dispatch<React.SetStateAction<User | null>>
): Promise<void> {
  try {
    const getRes = await usersApi.get(accessToken, baseUser.userId);
    const perms = getRes.data?.effectivePermissions ?? [];
    const withPerms: User = { ...baseUser, effectivePermissions: perms };
    setUserState((prev) => {
      if (prev?.userId !== baseUser.userId) return prev;
      saveStored(withPerms);
      return withPerms;
    });
  } catch (err) {
    console.warn('Could not load effective permissions; try refreshing.', err);
    setUserState((prev) => {
      if (!prev) return prev;
      const next = { ...prev, effectivePermissions: [] };
      saveStored(next);
      return next;
    });
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Read the persisted profile once, on first render, so the stored user (or "nothing to restore")
  // is known immediately instead of being copied into state by the restore effect.
  const [storedAtMount] = useState(loadStored);
  const [user, setUserState] = useState<User | null>(() => storedAtMount?.user ?? null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isRestored, setIsRestored] = useState(() => !storedAtMount?.user);
  const [pending2FA, setPending2FA] = useState<Pending2FA | null>(null);
  const toast = useToast();

  useEffect(() => {
    const stored = storedAtMount;
    if (!stored?.user) return;

    let cancelled = false;
    setRefreshInProgress(true);

    const keepOnboardingWithoutSession = (): boolean => {
      if (!isCompletingOnboardingSession(stored.user)) return false;
      const legacyToken = stored.legacyAccessToken ?? readLegacyAccessToken();
      return !legacyToken || !isAccessTokenStillValid(legacyToken);
    };

    const applySession = (user: User, accessToken: string) => {
      if (cancelled) return;
      setUserState(user);
      setToken(accessToken);
      saveStored(user);
    };

    const clearStoredSession = () => {
      if (cancelled) return;
      setUserState(null);
      setToken(null);
      saveStored(null);
    };

    void (async () => {
      const outerRounds = 4;

      for (let round = 0; round < outerRounds; round += 1) {
        if (cancelled) return;
        try {
          const data = await restoreSessionWithRetry(() =>
            authApi.refresh({ sessionRestore: true })
          );
          applySession(data.user, data.accessToken);
          return;
        } catch (err) {
          if (cancelled) return;

          const legacyToken = stored.legacyAccessToken ?? readLegacyAccessToken();
          if (legacyToken && isAccessTokenStillValid(legacyToken)) {
            applySession(stored.user, legacyToken);
            return;
          }

          if (err instanceof SessionRefreshUnauthorizedError) {
            if (keepOnboardingWithoutSession()) {
              return;
            }
            clearStoredSession();
            return;
          }

          if (round < outerRounds - 1) {
            await delay(600 * (round + 1));
            continue;
          }

          console.warn('Session refresh failed after retries.', err);
          if (keepOnboardingWithoutSession()) {
            return;
          }
          clearStoredSession();
        }
      }
    })().finally(() => {
      if (!cancelled) {
        setRefreshInProgress(false);
        setIsRestored(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [storedAtMount]);

  /* After restore or login: refresh current user from API (profile image, address, permissions). */
  useEffect(() => {
    if (!token || !user?.userId || user.status !== 'active') return;
    let cancelled = false;
    usersApi
      .get(token, user.userId)
      .then((res) => {
        if (cancelled || !res.data?.user) return;
        const fresh = res.data.user;
        const perms = res.data.effectivePermissions ?? [];
        setUserState((prev) => {
          if (prev?.userId !== fresh.userId) return prev;
          const next: User = { ...prev, ...fresh, effectivePermissions: perms };
          saveStored(next);
          return next;
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('Could not refresh session user profile.', err);
        setUserState((prev) => {
          if (!prev) return prev;
          if (prev.role?.startsWith('role_') && prev.effectivePermissions === undefined) {
            const next = { ...prev, effectivePermissions: [] };
            saveStored(next);
            return next;
          }
          return prev;
        });
      });
    return () => {
      cancelled = true;
    };
  }, [token, user?.userId, user?.status]);

  // Proactively refresh the access token 5 minutes before its 1-hour expiry.
  // This prevents the token from expiring mid-session on active users.
  useEffect(() => {
    if (!token) return;
    const ACCESS_TOKEN_LIFETIME_MS = 60 * 60 * 1000; // 1h — matches Lambda signAuthTokens
    const REFRESH_BUFFER_MS = 5 * 60 * 1000;         // refresh 5 min early
    const timerId = setTimeout(() => {
      authApi.refresh().then((res) => {
        if (res.success && res.data) {
          setUserState(res.data.user);
          setToken(res.data.accessToken);
          saveStored(res.data.user);
        }
      }).catch(() => {
        // Refresh cookie expired — the user will be prompted to log in on the next API call.
      });
    }, ACCESS_TOKEN_LIFETIME_MS - REFRESH_BUFFER_MS);
    return () => clearTimeout(timerId);
  }, [token]);

  const setUser = useCallback((u: User | null) => {
    setUserState(u);
    if (!u) {
      setToken(null);
    }
    saveStored(u);
  }, []);

  const setSession = useCallback((u: User, t: string) => {
    setUserState(u);
    setToken(t);
    saveStored(u);
  }, []);

  const mark2FAEnabled = useCallback((method?: 'totp' | 'sms') => {
    setUserState((prev) => {
      if (!prev) return prev;
      const next = {
        ...prev,
        twoFactorEnabled: true,
        ...(method ? { twoFactorMethod: method } : {}),
      };
      saveStored(next);
      return next;
    });
  }, []);

  const login = useCallback(async (email: string, password: string, persistSession = false) => {
    setIsLoading(true);
    setPending2FA(null);
    try {
      const res = await authApi.login({ email, password, persistSession });
      if (!res.success || !res.data) throw new Error(res.error?.message ?? 'Login failed');
      const data = res.data;
      if ('requiresVerification' in data && data.requiresVerification) {
        const u = onboardingUserFromLogin(data);
        setUserState(u);
        saveStored(u);
        return;
      }
      if ('requires2FA' in data && data.requires2FA && 'tempToken' in data && data.user) {
        setPending2FA({
          tempToken: data.tempToken,
          twoFactorMethod: data.twoFactorMethod ?? 'totp',
          canRememberDevice: data.canRememberDevice === true,
          user: data.user,
        });
        return;
      }
      const loginData = data as { user: User; accessToken: string };
      const u = loginData.user;
      const accessToken = loginData.accessToken;
      setUserState(u);
      setToken(accessToken);
      saveStored(u);
      await loadAndApplyPermissions(accessToken, u, setUserState);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const complete2FALogin = useCallback(async (code: string, rememberDevice = false) => {
    if (!pending2FA) return;
    setIsLoading(true);
    try {
      const res = await authApi.verify2FALogin(pending2FA.tempToken, code, rememberDevice);
      if (!res.success || !res.data) throw new Error(res.error?.message ?? 'Verification failed');
      const { user: u, accessToken } = res.data;
      setUserState(u);
      setToken(accessToken);
      saveStored(u);
      setPending2FA(null);
      await loadAndApplyPermissions(accessToken, u, setUserState);
    } finally {
      setIsLoading(false);
    }
  }, [pending2FA]);

  const clearPending2FA = useCallback(() => {
    setPending2FA(null);
  }, []);

  const logout = useCallback(async () => {
    const startedAt = Date.now();
    setIsLoggingOut(true);
    try {
      await authApi.logout(token);
      await ensureMinSigningOutVisible(startedAt);
      toast.success('Successfully logged out');
    } catch (err) {
      await ensureMinSigningOutVisible(startedAt);
      toast.error(err instanceof Error ? err.message : 'Logout failed. You have been logged out locally.');
    } finally {
      setUserState(null);
      setToken(null);
      setPending2FA(null);
      saveStored(null);
      setIsLoggingOut(false);
    }
  }, [token, toast]);

  const clearSession = useCallback(() => {
    setUserState(null);
    setToken(null);
    setPending2FA(null);
    saveStored(null);
  }, []);

  const refreshSession = useCallback(async () => {
    setRefreshInProgress(true);
    try {
      const res = await authApi.refresh();
      if (!res.success || !res.data) {
        throw new Error(res.error?.message ?? 'Refresh failed');
      }
      const { user: u, accessToken: t } = res.data;
      setUserState(u);
      setToken(t);
      saveStored(u);
      if (u.status === 'active') {
        await loadAndApplyPermissions(t, u, setUserState);
      }
    } finally {
      setRefreshInProgress(false);
    }
  }, []);

  const register = useCallback(
    async (data: RegisterRequest) => {
      setIsLoading(true);
      try {
        const res = await authApi.register(data);
        if (!res.success || !res.data) throw new Error(res.error?.message ?? 'Registration failed');
        const { user: u, email: regEmail, emailCode, phoneCode, isNewRegistration } = res.data;
        const nextUser = u ?? onboardingUserStub(regEmail ?? data.email);
        setUserState(nextUser);
        saveStored(nextUser);
        return {
          emailCode,
          phoneCode,
          isNewRegistration: isNewRegistration === true,
        };
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const permissionsResolved = useMemo(
    () =>
      !user ||
      !user.role?.startsWith('role_') ||
      user.effectivePermissions !== undefined,
    [user]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isLoading,
      isLoggingOut,
      isRestored,
      permissionsResolved,
      pending2FA,
      login,
      complete2FALogin,
      clearPending2FA,
      logout,
      clearSession,
      register,
      setUser,
      setSession,
      mark2FAEnabled,
      refreshSession,
    }),
    [
      user,
      token,
      isLoading,
      isLoggingOut,
      isRestored,
      permissionsResolved,
      pending2FA,
      login,
      complete2FALogin,
      clearPending2FA,
      logout,
      clearSession,
      register,
      setUser,
      setSession,
      mark2FAEnabled,
      refreshSession,
    ]
  );

  return (
    <AuthContext value={value}>
      {children}
      <SigningOutOverlay />
    </AuthContext>
  );
}
